import { useState } from 'react'
import { apiError } from '../lib/errors'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Package, AlertTriangle, X, ArrowDown, ArrowUp, BarChart2 } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { inventoryApi, type InventoryItem } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', card: 'var(--color-bg-card)', card2: 'var(--color-bg-card-2)',
  border: 'var(--color-border)', primary: 'var(--color-primary)',
  text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
  danger: 'var(--color-danger)', success: 'var(--color-success)',
}
const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px', color: C.text, fontSize: 13, outline: 'none',
}
const lbl: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.muted,
  marginBottom: 4, textTransform: 'uppercase' as const, letterSpacing: '0.06em',
}

const ITEM_CATEGORIES = ['Fertilizer','Pesticide','Fungicide','Herbicide','Tool','Equipment','Packaging','Seed','Other']

function ItemModal({ item, onClose }: { item: InventoryItem | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isEdit = !!item
  const [form, setForm] = useState({
    name: item?.name ?? '', category: item?.category ?? 'Fertilizer',
    unit: item?.unit ?? 'kg', quantity_on_hand: item?.quantity_on_hand?.toString() ?? '0',
    reorder_point: item?.reorder_point?.toString() ?? '', npk: item?.npk ?? '',
  })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: (data: Partial<InventoryItem>) =>
      (isEdit ? inventoryApi.update(item!.id, data) : inventoryApi.create(data)) as Promise<unknown>,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inventory'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) { setError('Name is required'); return }
    save.mutate({
      name: form.name, category: form.category, unit: form.unit,
      quantity_on_hand: parseFloat(form.quantity_on_hand) || 0,
      reorder_point: form.reorder_point ? parseFloat(form.reorder_point) : undefined,
      npk: form.npk || undefined,
    })
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 420, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{isEdit ? 'Edit item' : 'Add item'}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: 20, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={lbl}>Item name *</label><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Potassium Nitrate" required /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Category</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.category} onChange={e => set('category', e.target.value)}>
                {ITEM_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Unit</label><input style={inp} value={form.unit} onChange={e => set('unit', e.target.value)} placeholder="kg / L / bags" /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div><label style={lbl}>Qty on hand</label><input style={inp} type="number" min={0} step={0.1} value={form.quantity_on_hand} onChange={e => set('quantity_on_hand', e.target.value)} /></div>
            <div><label style={lbl}>Reorder point</label><input style={inp} type="number" min={0} step={0.1} value={form.reorder_point} onChange={e => set('reorder_point', e.target.value)} placeholder="Alert when below" /></div>
          </div>
          <div><label style={lbl}>NPK ratio</label><input style={inp} value={form.npk} onChange={e => set('npk', e.target.value)} placeholder="13-0-46" /></div>
          {error && <div style={{ fontSize: 12, color: C.danger, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={save.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.primary, border: 'none', color: C.bg, cursor: save.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {save.isPending ? 'Saving…' : isEdit ? 'Save' : 'Add item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function TransactionModal({ item, onClose }: { item: InventoryItem; onClose: () => void }) {
  const qc = useQueryClient()
  const [type, setType] = useState<'use' | 'restock'>('use')
  const [qty, setQty] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => inventoryApi.recordTransaction(item.id, {
      type, quantity: parseFloat(qty), notes,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['inventory'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed')),
  })

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 360, maxWidth: '95vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Record transaction — {item.name}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.faint }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['use', 'restock'] as const).map(t => (
              <button key={t} onClick={() => setType(t)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: type === t ? 600 : 400, cursor: 'pointer', background: type === t ? (t === 'use' ? 'rgba(248,113,113,0.15)' : 'rgba(76,175,80,0.15)') : C.card2, color: type === t ? (t === 'use' ? C.danger : C.success) : C.muted, border: `1px solid ${type === t ? (t === 'use' ? 'rgba(248,113,113,0.3)' : 'rgba(76,175,80,0.3)') : C.border}` }}>
                {t === 'use' ? '↓ Use' : '↑ Restock'}
              </button>
            ))}
          </div>
          <div>
            <label style={lbl}>Quantity ({item.unit})</label>
            <input style={inp} type="number" min={0} step={0.1} value={qty} onChange={e => setQty(e.target.value)} placeholder="0" autoFocus />
          </div>
          <div>
            <label style={lbl}>Notes</label>
            <input style={inp} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Zone B, April fertigation…" />
          </div>
          {error && <div style={{ fontSize: 12, color: C.danger, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <button onClick={() => { if (!qty) { setError('Enter quantity'); return } save.mutate() }} disabled={save.isPending} style={{ padding: '9px 0', borderRadius: 8, background: C.primary, border: 'none', color: C.bg, cursor: save.isPending ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
            {save.isPending ? 'Saving…' : 'Record'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function InventoryPage() {
  const [modal, setModal] = useState<InventoryItem | null | 'new'>(null)
  const [txItem, setTxItem] = useState<InventoryItem | null>(null)
  const [filterCat, setFilterCat] = useState('All')

  const { data, isLoading } = useQuery({
    queryKey: ['inventory', filterCat],
    queryFn: () => inventoryApi.list(filterCat === 'All' ? undefined : filterCat).then(r => r.data),
  })

  const items = data?.items ?? []
  const lowStock = items.filter(i => i.reorder_point != null && i.quantity_on_hand <= i.reorder_point!)
  const categories = new Set(items.map(i => i.category)).size
  const withReorder = items.filter(i => i.reorder_point != null).length

  // Top 8 items by quantity for chart
  const stockChartData = [...items]
    .sort((a, b) => b.quantity_on_hand - a.quantity_on_hand)
    .slice(0, 8)
    .map(i => ({
      name: i.name.length > 14 ? i.name.slice(0, 13) + '…' : i.name,
      qty: i.quantity_on_hand,
      isLow: i.reorder_point != null && i.quantity_on_hand <= i.reorder_point!,
    }))

  return (
    <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', background: C.bg }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif' }}>Inventory</h1>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: C.muted }}>Fertilizers, chemicals, and supplies — track stock and usage</p>
        </div>
        <button onClick={() => setModal('new')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.primary, border: 'none', color: C.bg, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={16} /> Add item
        </button>
      </div>

      {/* Stat chips */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total items', value: items.length, icon: Package, color: '#F5A623' },
          { label: 'Low stock', value: lowStock.length, icon: AlertTriangle, color: lowStock.length > 0 ? '#F87171' : '#6B6760' },
          { label: 'Categories', value: categories, icon: BarChart2, color: '#F5A623' },
          { label: 'With reorder point', value: withReorder, icon: Package, color: '#60A5FA' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <Icon size={14} color={color} />
              <span style={{ fontSize: 11, color: C.muted }}>{label}</span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Stock levels chart */}
      {stockChartData.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#F0EDE4', marginBottom: 4 }}>Stock Levels — Top Items</div>
          <div style={{ fontSize: 11, color: '#A8A49C', marginBottom: 14 }}>Quantity on hand (red = at or below reorder point)</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={stockChartData} margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
              <XAxis dataKey="name" tick={{ fill: '#A8A49C', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#A8A49C', fontSize: 11 }} axisLine={false} tickLine={false} width={36} />
              <Tooltip contentStyle={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
              <Bar dataKey="qty" name="Qty on hand" radius={[4, 4, 0, 0]}>
                {stockChartData.map((entry, i) => (
                  <Cell key={i} fill={entry.isLow ? '#F87171' : '#F5A623'} fillOpacity={entry.isLow ? 1 : 0.8} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {lowStock.length > 0 && (
        <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <AlertTriangle size={16} style={{ color: C.danger, flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 13, color: C.danger }}>
            <strong>{lowStock.length} item{lowStock.length > 1 ? 's' : ''} low on stock:</strong>{' '}
            {lowStock.map(i => `${i.name} (${i.quantity_on_hand} ${i.unit})`).join(', ')}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {['All', ...ITEM_CATEGORIES].map(cat => (
          <button key={cat} onClick={() => setFilterCat(cat)} style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, fontWeight: filterCat === cat ? 600 : 400, cursor: 'pointer', background: filterCat === cat ? C.primary : C.card, color: filterCat === cat ? C.bg : C.muted, border: `1px solid ${filterCat === cat ? C.primary : C.border}` }}>
            {cat}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: C.faint, fontSize: 14 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', border: `2px dashed ${C.border}`, borderRadius: 12 }}>
          <Package size={32} style={{ color: C.faint, marginBottom: 12, opacity: 0.5 }} />
          <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>No inventory items yet.</p>
          <p style={{ fontSize: 12, color: C.faint, margin: '6px 0 16px' }}>Track fertilizers, chemicals, and supplies to monitor usage and reorder points.</p>
          <button onClick={() => setModal('new')} style={{ padding: '9px 18px', borderRadius: 8, background: C.primary, border: 'none', color: C.bg, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Add first item</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map(item => {
            const isLow = item.reorder_point != null && item.quantity_on_hand <= item.reorder_point!
            return (
              <div key={item.id} style={{ background: C.card, border: `1px solid ${isLow ? 'rgba(248,113,113,0.3)' : C.border}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: C.text }}>{item.name}</p>
                    <span style={{ padding: '1px 7px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: C.card2, color: C.faint, border: `1px solid ${C.border}` }}>{item.category}</span>
                    {item.npk && <span style={{ fontSize: 11, color: C.faint }}>NPK {item.npk}</span>}
                  </div>
                  {item.reorder_point != null && (
                    <p style={{ margin: 0, fontSize: 11, color: isLow ? C.danger : C.faint }}>
                      {isLow ? '⚠ Low stock — ' : ''}reorder at {item.reorder_point} {item.unit}
                    </p>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: isLow ? C.danger : C.text }}>{item.quantity_on_hand}</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.faint }}>{item.unit}</p>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => setTxItem(item)} title="Record use/restock" style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '6px 10px', borderRadius: 7, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 12 }}>
                    <ArrowDown size={12} /><ArrowUp size={12} />
                  </button>
                  <button onClick={() => setModal(item)} style={{ padding: '6px 10px', borderRadius: 7, background: C.card2, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 12 }}>Edit</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {modal !== null && <ItemModal item={modal === 'new' ? null : modal} onClose={() => setModal(null)} />}
      {txItem && <TransactionModal item={txItem} onClose={() => setTxItem(null)} />}
    </main>
  )
}
